import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import isBetween from 'dayjs/plugin/isBetween';

import { IComponentLocationsList } from '@/types/cms';
import { getFacetCode } from '@lamesarv-sdk/tools';

import { usStateCityToTimezone } from '../constants/locations';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

export const parseLocations = <T>(
  type: string,
  locationsList: NonNullable<IComponentLocationsList['locations']>,
): T => {
  const locations: T = {} as T;

  for (const item of locationsList) {
    if (!item?.location?.title) continue;

    let location = {};
    let state = '';

    if (type === 'sales') {
      state = item.location.sales_state;
      location = {
        address: item.location.sales_address,
        city: item.location.sales_city,
        state: item.location.sales_state,
        zipcode: item.location.sales_zipcode,
        schedule_saturday: item.location.sales_schedule_saturday,
        schedule_sunday: item.location.sales_schedule_sunday,
        schedule_weekdays: item.location.sales_schedule_weekdays,
        geo_location: item.location.sales_geo_location
          ? (([lat, lng]) => ({ lat, lng }))(
              item.location.sales_geo_location
                .split(',')
                .map((item) => item.trim())
                .map(Number),
            )
          : undefined,
      };
    }

    if (type === 'service') {
      state = item.location.service_state;
      location = {
        address: item.location.service_address,
        city: item.location.service_city,
        state: item.location.service_state,
        zipcode: item.location.service_zipcode,
        schedule_saturday: item.location.service_schedule_saturday,
        schedule_sunday: item.location.service_schedule_sunday,
        schedule_weekdays: item.location.service_schedule_weekdays,
        geo_location: item.location.service_geo_location
          ? (([lat, lng]) => ({ lat, lng }))(
              item.location.service_geo_location
                .split(',')
                .map((item) => item.trim())
                .map(Number),
            )
          : undefined,
      };
    }

    location['title'] = item.location.title;
    location['url'] = item.url;
    location['target_blank'] = item.target_blank;

    if (!locations[state]) {
      locations[state] = [];
    }

    locations[state].push(location);
  }

  return locations;
};

export const getTimezone = (state: string, city: string) => {
  const stateObj = usStateCityToTimezone[getFacetCode(state)];

  if (!stateObj) return 'America/Phoenix';

  return stateObj[getFacetCode(city)] || stateObj['*'];
};

export const parseTimeString = (timeStr: string): { hour: number; minute: number } | null => {
  if (!timeStr) return null;

  const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(am|pm)/i);

  if (!match) return null;

  let hour = parseInt(match[1]);
  const minute = parseInt(match[2]);
  const isPM = match[3].toLowerCase() === 'pm';

  if (isPM && hour !== 12) hour += 12;
  if (!isPM && hour === 12) hour = 0;

  return { hour, minute };
};

export interface IScheduleDaysProps {
  state: string;
  city: string;
  schedule_sunday: string;
  schedule_weekdays: string;
  schedule_saturday: string;
  asTimeZone: string;
  customNow?: dayjs.Dayjs;
}

export interface IScheduleDay {
  start?: dayjs.Dayjs;
  startLocal?: dayjs.Dayjs;
  end?: dayjs.Dayjs;
  endLocal?: dayjs.Dayjs;
  defaultClosed?: boolean;
}

export const getScheduleDay = ({
  state,
  city,
  schedule_sunday,
  schedule_weekdays,
  schedule_saturday,
  asTimeZone,
  customNow,
}: IScheduleDaysProps): IScheduleDay => {
  const timezone = getTimezone(state, city);
  const now = customNow || dayjs().tz(asTimeZone);

  let startCms: string, endCms: string;

  if (now.day() === 0) {
    if (schedule_sunday) {
      [startCms, endCms] = schedule_sunday.split('-').map((time) => time.trim());
    } else {
      return { defaultClosed: true };
    }
  }

  if (now.day() >= 1 && now.day() <= 5) {
    if (schedule_weekdays) {
      [startCms, endCms] = schedule_weekdays.split('-').map((time) => time.trim());
    } else {
      return { defaultClosed: true };
    }
  }

  if (now.day() === 6) {
    if (schedule_saturday) {
      [startCms, endCms] = schedule_saturday.split('-').map((time) => time.trim());
    } else {
      return { defaultClosed: true };
    }
  }

  const startTimeParsed = parseTimeString(startCms);
  const start: dayjs.Dayjs = dayjs(`${now.format('YYYY-MM-DD')}T${startTimeParsed.hour}:${startTimeParsed.minute}`).tz(
    timezone,
    true,
  );
  const startLocal: dayjs.Dayjs = start.clone().tz(asTimeZone);

  const endTimeParsed = parseTimeString(endCms);
  const end: dayjs.Dayjs = dayjs(`${now.format('YYYY-MM-DD')}T${endTimeParsed.hour}:${endTimeParsed.minute}`).tz(
    timezone,
    true,
  );
  const endLocal: dayjs.Dayjs = end.clone().tz(asTimeZone);

  return {
    start,
    startLocal,
    end,
    endLocal,
    defaultClosed: false,
  };
};
